import ScButton from "@/src/components/ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import {
  CloseCircle,
  InfoCircle,
  TickCircle,
  Warning2,
} from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";

type InfoModalVariant = "success" | "error" | "info" | "warning";

type InfoModalProps = Readonly<{
  visible: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  variant?: InfoModalVariant;
  onConfirm: () => void;
}>;

const iconBgClass: Record<InfoModalVariant, string> = {
  success: "bg-sage",
  error: "bg-blush",
  warning: "bg-wheat",
  info: "bg-lavender",
};

const iconColorClass: Record<InfoModalVariant, string> = {
  success: "--color-jade-default",
  error: "--color-crimson-default",
  warning: "--color-coral-default",
  info: "--color-primary-default",
};

const variantIcon: Record<
  InfoModalVariant,
  React.ComponentType<{ size: number; color: string }>
> = {
  success: TickCircle,
  error: Warning2,
  warning: Warning2,
  info: InfoCircle,
};

const InfoModal = ({
  visible,
  title,
  description,
  confirmText,
  variant = "info",
  onConfirm,
}: InfoModalProps) => {
  const { t } = useTranslation();
  const Icon = variantIcon[variant];

  const { themeVars, varStyle } = useTheme();

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onConfirm}
    >
      <View
        style={varStyle}
        className="flex-1 justify-center items-center bg-black/40"
      >
        <View className="w-[90%] max-w-md bg-chalk rounded-2xl p-6 gap-4">
          <View className="flex-row justify-between items-center">
            <Text className="text-xl font-semibold text-onyx flex-1 pr-2">
              {t(title)}
            </Text>
            <Pressable
              onPress={onConfirm}
              hitSlop={8}
              className="p-1.5 rounded-full hover:bg-ash-3"
            >
              <CloseCircle size={24} className="text-onyx" />
            </Pressable>
          </View>

          <View className="items-center gap-2">
            <View
              className={`w-16 h-16 rounded-full items-center justify-center ${iconBgClass[variant]}`}
            >
              <Icon
                size={36}
                color={themeVars[iconColorClass[variant]] as string}
              />
            </View>

            {description && (
              <Text className="text-base text-center text-slate leading-relaxed">
                {t(description)}
              </Text>
            )}
          </View>

          <View className="items-center">
            <ScButton text={confirmText || t("BTN_OK")} onPress={onConfirm} />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default InfoModal;
